import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AgentAvatar from "../AgentAvatar";
import useAgentStore from "../useAgentStore";
import useQueuedMessageStore from "../useQueuedMessageStore";
import { MessageImageThumbnails } from "./MessageBubbles";
import "./QueuedMessageTray.css";

const EMPTY_QUEUE = [];

function canEdit(item, currentUserId) {
  return (
    item?.sourceType === "HUMAN" &&
    Boolean(currentUserId) &&
    item.sourceId === currentUserId
  );
}

export default function QueuedMessageTray({
  chatSessionId,
  surface,
  paused,
  currentUserId,
}) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const edit = useQueuedMessageStore((state) => state.edit);
  const cancel = useQueuedMessageStore((state) => state.cancel);
  const dispatchNext = useQueuedMessageStore((state) => state.dispatchNext);
  const fetchQueue = useQueuedMessageStore((state) => state.fetchQueue);
  const clearSession = useQueuedMessageStore((state) => state.clearSession);
  const items = useQueuedMessageStore(
    (state) => state.bySessionId[chatSessionId] || EMPTY_QUEUE
  );
  const externalError = useQueuedMessageStore(
    (state) => state.errorBySessionId[chatSessionId] || ""
  );

  useEffect(() => {
    if (!chatSessionId) return undefined;
    fetchQueue(chatSessionId, surface);
    return () => clearSession(chatSessionId);
  }, [chatSessionId, clearSession, fetchQueue, surface]);

  if (!items?.length && !externalError) return null;

  const beginEdit = (item) => {
    setEditingId(item.queueId);
    setDraft(item.message || "");
    setError("");
  };

  const saveEdit = async (item) => {
    if (!draft.trim()) return;
    setBusyId(item.queueId);
    setError("");
    try {
      await edit(chatSessionId, surface, item.queueId, draft, item.version);
      setEditingId(null);
    } catch (nextError) {
      if (nextError?.status === 409) {
        await fetchQueue(chatSessionId, surface);
      }
      setError(nextError?.message || "Failed to edit queued message");
    } finally {
      setBusyId(null);
    }
  };

  const cancelItem = async (item) => {
    setBusyId(item.queueId);
    setError("");
    try {
      await cancel(chatSessionId, surface, item.queueId);
    } catch (nextError) {
      setError(nextError?.message || "Failed to cancel queued message");
    } finally {
      setBusyId(null);
    }
  };

  const sendNext = async (item) => {
    setBusyId(item.queueId);
    setError("");
    try {
      const result = await dispatchNext(chatSessionId, surface);
      if (result?.status === "ACCEPTED") {
        const agentStore = useAgentStore.getState();
        agentStore._applyLifecycle({
          kind: "optimistic",
          sessionId: chatSessionId,
          action: "send",
        });
        useAgentStore.setState((state) => ({
          sessionDetail:
            state.sessionDetail?.chatSessionId === chatSessionId
              ? { ...state.sessionDetail, status: "IN_PROGRESS" }
              : state.sessionDetail,
        }));
        agentStore.subscribeToSession(chatSessionId, {
          reconnectWhileSubscribed: true,
          forceReconnect: true,
        });
      }
    } catch (nextError) {
      setError(nextError?.message || "Failed to send queued message");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box className="queued-message-tray">
      <Typography className="queued-message-tray__title">Queued</Typography>
      {(items || []).map((item, index) => {
        const isEditing = editingId === item.queueId;
        const itemBusy = busyId === item.queueId;
        const sourceName =
          item.sourceDisplayName ||
          (item.sourceType === "AGENT" ? "Agent" : "User");
        return (
          <Box className="queued-message-tray__row" key={item.queueId}>
            <AgentAvatar
              agentId={item.sourceType === "AGENT" ? item.sourceId : undefined}
              name={sourceName}
              size={24}
              borderRadius="5px"
            />
            <Box className="queued-message-tray__content">
              <Typography className="queued-message-tray__author">
                {sourceName}
              </Typography>
              {isEditing ? (
                <TextField
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") setEditingId(null);
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      saveEdit(item);
                    }
                  }}
                  size="small"
                  multiline
                  autoFocus
                  fullWidth
                />
              ) : (
                <Typography className="queued-message-tray__message">
                  {item.message}
                </Typography>
              )}
              <MessageImageThumbnails imageUrls={item.imageUrls} />
            </Box>
            <Box className="queued-message-tray__actions">
              {paused && index === 0 && !isEditing && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={itemBusy}
                  onClick={() => sendNext(item)}
                >
                  Send next
                </Button>
              )}
              {isEditing ? (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={itemBusy || !draft.trim()}
                    onClick={() => saveEdit(item)}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={itemBusy}
                    onClick={() => setEditingId(null)}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  {canEdit(item, currentUserId) && (
                    <Tooltip title="Edit queued message">
                      <IconButton
                        size="small"
                        disabled={itemBusy}
                        onClick={() => beginEdit(item)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Cancel queued message">
                    <IconButton
                      size="small"
                      disabled={itemBusy}
                      onClick={() => cancelItem(item)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
        );
      })}
      {(error || externalError) && (
        <Typography className="queued-message-tray__error">
          {error || externalError}
        </Typography>
      )}
    </Box>
  );
}
